<template>
  <v-navigation-drawer v-model="activator"
                       temporary
                       retain-focus
                       capture-focus
                       role="dialog"
                       aria-modal="true"
                       :order="LAYOUT_ORDER"
                       :aria-labelledby="ariaLabelledby"
                       location="right"
                       :width="drawerWidth"
                       class="app-drawer__nav"
                       v-bind="attrs.componentAttrs"
  >
    <slot name="default">
      <div class="app-drawer" :data-locator="attrs.dataLocator">
        <div v-if="!hideHeader" class="app-drawer__header" :class="{ 'app-drawer__header--with-tabs': hasTabs }">
          <h2 :id="titleId" class="app-drawer__title text-text-primary text-truncate" data-locator="drawer-title">
            <!-- @slot for title content -->
            <slot name="title"></slot>
          </h2>
          <div class="app-drawer__header-actions">
            <!-- @slot for an optional header action (button or other) -->
            <div v-if="$slots['header-action']" data-locator="drawer-header-action">
              <slot name="header-action"></slot>
            </div>
            <app-button
              v-if="closeButton"
              type="icon"
              class="overlay-close-button mt-n1"
              data-locator="close-button"
              :aria-label="$t('common.close')"
              :disabled="cancelDisabled"
              size="small"
              @click="cancel"
            >
              <template #left-icon>
                <app-material-icon name="close_small" size="lg" />
              </template>
            </app-button>
          </div>
        </div>

        <v-tabs
          v-if="hasTabs"
          v-model="activeTab"
          class="app-drawer__tabs"
        >
          <v-tab
            v-for="tabItem in tabs"
            :key="tabItem.value"
            :value="tabItem.value"
            :data-locator="`${tabItem.value}-tab`"
            :ripple="false"
          >
            {{ tabItem.label }}
          </v-tab>
        </v-tabs>

        <div class="app-drawer__body">
          <div class="app-drawer__content"
               :class="{
                 'app-drawer__content--flush': noBodyPadding,
                 'app-drawer__content--fill': fillBody,
               }"
               data-locator="drawer-content"
          >
            <!-- With tabs: content is driven by the active tab through per-tab slots -->
            <v-tabs-window v-if="hasTabs" v-model="activeTab">
              <v-tabs-window-item
                v-for="tabItem in tabs"
                :key="tabItem.value"
                :value="tabItem.value"
                :data-locator="`${tabItem.value}-tab-content`"
              >
                <!-- @slot dynamic per-tab content, named `tab-<value>` -->
                <slot :name="`tab-${tabItem.value}`"></slot>
              </v-tabs-window-item>
            </v-tabs-window>
            <!-- @slot for body content (used when there are no tabs) -->
            <slot v-else name="body"></slot>
          </div>
          <!-- Fade-out gradient hinting at scrollable overflow. Pointless, and in the way,
               when the body does not scroll because its content handles it -->
          <div v-if="!fillBody" class="app-drawer__fade" aria-hidden="true"></div>
        </div>

        <div v-if="!hideFooter && !$slots['footer']" class="app-drawer__actions">
          <!-- @slot for an optional footer link, aligned to the left -->
          <div v-if="$slots['footer-link']" class="app-drawer__footer-link" data-locator="drawer-footer-link">
            <slot name="footer-link"></slot>
          </div>
          <app-button
            v-if="!okOnly"
            type="ghost"
            variant="neutral"
            :disabled="cancelDisabled"
            :loading="cancelLoading"
            data-locator="cancel-button"
            @click="cancel"
          >
            {{ computedCancelText }}
          </app-button>
          <app-button
            v-if="!cancelOnly"
            type="primary"
            variant="brand"
            :disabled="okDisabled"
            :loading="okLoading"
            data-locator="confirm-button"
            @click="ok"
          >
            {{ computedOkText }}
          </app-button>
        </div>
        <!-- hideFooter wins over a custom footer, so a drawer whose actions depend on the
             active tab can declare the slot once and still show no footer bar at all on a tab
             that has no action. -->
        <div v-if="$slots['footer'] && !hideFooter" class="app-drawer__actions">
          <!-- @slot for a fully custom footer -->
          <slot name="footer"></slot>
        </div>
      </div>
    </slot>
  </v-navigation-drawer>
</template>

<script lang="ts">
import { defineComponent, useId, type PropType } from 'vue'

import { useDataLocator } from '@/common/utils/useDataLocator'
import AppButton from '@/design-system/buttons/AppButton.vue'
import { DRAWER_SIZES, type AppDrawerSize, type AppDrawerTab } from '@/design-system/drawers/types'
import AppMaterialIcon from '@/design-system/icons/AppMaterialIcon.vue'

export type { AppDrawerSize, AppDrawerTab } from '@/design-system/drawers/types'

/**
 * Vuetify layers layout items by `order` first, then by DOM order, and derives their z-index
 * from that rank (`1000 + layers * 2 - index * 2`, the scrim sitting one below the drawer).
 * A drawer declared inside a page therefore ranks after the app's own navigation drawer and
 * would slide out *under* the sidebar, scrim included. A negative order puts the drawer first
 * so both it and its scrim cover the layout chrome. Temporary drawers report a layout size of
 * 0, so this only changes the stacking, never the page geometry.
 */
const LAYOUT_ORDER = -1

/**
 * The page behind the scrim must not scroll: the drawer is modal (role=dialog, aria-modal,
 * focus trap), and v-navigation-drawer brings no scroll strategy of its own, unlike v-dialog.
 * The counter is module-level so two open drawers, or one closing while another is still open,
 * cannot release the lock too early.
 */
const SCROLL_LOCK_CLASS = 'overflow-hidden'
let lockedDrawerCount = 0

/**
 * Design-system drawer (side panel) based on Vuetify v-navigation-drawer.
 * Mirrors the AppModal API but slides in from the right edge at full height.
 * Uses the standard v-model contract (modelValue / update:modelValue).
 *
 * The side is deliberately not configurable: design requires every drawer to open on the
 * right, so the rule is enforced here rather than left to each consumer.
 *
 * Accessibility note: role, aria-modal, retain-focus and capture-focus are all set by hand on
 * the drawer. Unlike v-dialog, v-navigation-drawer renders no dialog semantics at all, and
 * Vuetify's focusTrap props are bare Booleans that default to false, so nothing is free here.
 * Beware of adding a comment at the root of the template: it would make the component
 * multi-root and break attribute fallthrough.
 *
 * @displayName Drawer
 */
export default defineComponent({
  name: 'AppDrawer',
  components: {
    AppButton,
    AppMaterialIcon,
  },
  inheritAttrs: false,
  setup () {
    const { attrs } = useDataLocator()
    // Names the drawer for assistive technologies: role="dialog" alone carries no accessible
    // name. Only referenced when the title is actually rendered, see ariaLabelledby.
    const titleId = useId()
    return { attrs, titleId, LAYOUT_ORDER }
  },
  props: {
    /**
     * @model
     * Visibility state of the drawer
     */
    modelValue: {
      type: Boolean,
      default: false,
    },
    /**
     * Width of the drawer
     * @values xs (400px), sm (460px), md (640px, default), lg (800px), xl (960px)
     */
    size: {
      type: String as PropType<AppDrawerSize>,
      validator: (value: string) => Object.keys(DRAWER_SIZES).includes(value),
      default: 'md',
    },
    /**
     * Clicking outside of the element will not deactivate it. There is no Esc to neutralise
     * here, v-navigation-drawer never handled it. Defaulting to true mirrors AppModal, where
     * design chose to keep closing explicit through the close button or the cancel action.
     */
    persistent: {
      type: Boolean,
      default: true,
    },
    /**
     * If the close button is displayed
     */
    closeButton: {
      type: Boolean,
      default: true,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    /**
     * Removes the padding around the body, for a content that lays itself out edge to edge:
     * a full-width bar, a list that scrolls under a sticky header, a file tree. Such a content
     * brings its own padding.
     */
    noBodyPadding: {
      type: Boolean,
      default: false,
    },
    /**
     * Hands the body height down to the content instead of letting it grow and scrolling it
     * here. For a content that lays itself out against that height: a list scrolling between a
     * fixed bar and a form, an editor filling the space it is given. Such a content owns its
     * own scrolling, and Vuetify's tab window is stretched along the way.
     */
    fillBody: {
      type: Boolean,
      default: false,
    },
    /**
     * Optional tabs displayed under the header
     */
    tabs: {
      type: Array as PropType<AppDrawerTab[]>,
      default: () => [],
    },
    /**
     * @model
     * Active tab value (use with v-model:tab)
     */
    tab: {
      type: [String, Number] as PropType<string | number | undefined>,
      default: undefined,
    },
    /**
     * Text displayed inside the cancel button
     */
    cancelText: {
      type: String,
      default: undefined,
    },
    /**
     * Set loading state of the cancel button
     */
    cancelLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * Removes the ability to click or target the cancel button
     */
    cancelDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Ok button will not be displayed if set to true
     */
    cancelOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * Cancel button will not be displayed if set to true
     */
    okOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * Text displayed inside the ok button
     */
    okText: {
      type: String,
      default: undefined,
    },
    /**
     * Set loading state of the ok button
     */
    okLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * Removes the ability to click or target the ok button
     */
    okDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'update:tab', 'cancel', 'ok'],
  data () {
    return {
      internalTab: undefined as string | number | undefined,
      elementFocusedBeforeOpening: null as HTMLElement | null,
      hasLockedPageScroll: false,
    }
  },
  watch: {
    /**
     * Restores keyboard focus to whatever opened the drawer once it closes. Nothing in
     * v-navigation-drawer does this, so without it the focus falls back to <body> and tab
     * navigation restarts from the top of the page.
     */
    modelValue (isOpen: boolean) {
      if (isOpen) {
        this.lockPageScroll()
        const activeElement = document.activeElement
        this.elementFocusedBeforeOpening = activeElement instanceof HTMLElement ? activeElement : null
        return
      }

      this.unlockPageScroll()

      const elementToRefocus = this.elementFocusedBeforeOpening
      this.elementFocusedBeforeOpening = null
      // The trigger may have been removed meanwhile (a menu item, a deleted row...)
      if (!elementToRefocus?.isConnected) return

      this.$nextTick(() => elementToRefocus.focus({ preventScroll: true }))
    },
  },
  mounted () {
    // A drawer can be mounted already open, in which case the watcher never fires
    if (this.modelValue) {
      this.lockPageScroll()
    }
  },
  beforeUnmount () {
    this.unlockPageScroll()
  },
  computed: {
    activator: {
      get (): boolean {
        return this.modelValue
      },
      set (value: boolean) {
        // v-navigation-drawer only drives this setter on outside interaction: a scrim
        // click or a route change. It has no Esc handling of its own, unlike v-dialog.
        // When persistent, ignore those close requests so the drawer stays open, which
        // matches the decision taken with design for AppModal: closing stays explicit.
        if (this.persistent && !value) {
          return
        }
        this.$emit('update:modelValue', value)
      },
    },
    /**
     * Points at the title only when that title is really in the DOM, otherwise the drawer
     * would carry a dangling or empty reference and end up with no accessible name at all.
     * Three ways to lose it: a `default` slot replaces the whole shell, header included;
     * `hideHeader` drops the header; no `title` slot leaves the heading empty.
     * Left undefined in those cases so a consumer can still pass its own aria-label.
     */
    ariaLabelledby (): string | undefined {
      const rendersTitle = !this.$slots['default'] && !this.hideHeader && !!this.$slots['title']
      return rendersTitle ? this.titleId : undefined
    },
    activeTab: {
      get () {
        return this.tab ?? this.internalTab ?? this.tabs[0]?.value
      },
      set (value: string | number) {
        this.internalTab = value
        this.$emit('update:tab', value)
      },
    },
    hasTabs (): boolean {
      return this.tabs.length > 0
    },
    computedCancelText (): string {
      return this.cancelText ?? this.$t('common.cancel')
    },
    computedOkText (): string {
      return this.okText ?? this.$t('common.validate')
    },
    /**
     * On mobile viewports the drawer spans the full width so the sized
     * variants (xs..xl) never overflow narrow screens. Uses Vuetify's `mobile`
     * flag so this stays aligned with the `.v-navigation-drawer--mobile` class
     * that drives the full-width style (both follow the same mobileBreakpoint).
     */
    isResponsive (): boolean {
      return this.$vuetify.display.mobile
    },
    drawerWidth (): string | undefined {
      return this.isResponsive ? undefined : DRAWER_SIZES[this.size]
    },
  },
  methods: {
    lockPageScroll () {
      if (this.hasLockedPageScroll) return

      this.hasLockedPageScroll = true
      lockedDrawerCount += 1
      document.documentElement.classList.add(SCROLL_LOCK_CLASS)
    },
    unlockPageScroll () {
      if (!this.hasLockedPageScroll) return

      this.hasLockedPageScroll = false
      lockedDrawerCount -= 1
      if (lockedDrawerCount === 0) {
        document.documentElement.classList.remove(SCROLL_LOCK_CLASS)
      }
    },
    /**
     * Gets called when the user clicks the cancel or close button
     * @event cancel
     */
    cancel () {
      if (!this.cancelDisabled) {
        this.$emit('cancel')
      }
    },
    /**
     * Gets called when the user clicks the ok button
     * @event ok
     */
    ok () {
      if (!this.okLoading && !this.okDisabled) {
        this.$emit('ok')
      }
    },
  },
})
</script>

<style scoped lang="scss">
@import "vuetify/settings";
@import "@/scss/border-tokens";
@import "@/scss/typography-tokens";


.app-drawer__nav {
  &.v-navigation-drawer--mobile {
    width: 100% !important;

    // Vuetify hides an inactive drawer by translating it by the value of its `width` prop,
    // which this variant deliberately leaves unset to span the full width. Vuetify then falls
    // back to its own 256px default, so the panel would slide out by 256px and stay on screen
    // over the remaining width. Vuetify's default mobileBreakpoint is lg (1280px), so that
    // affects every ordinary laptop window, not just phones.
    &:not(.v-navigation-drawer--active) {
      transform: translateX(100%) !important;
    }
  }

  // Let the drawer content fill the height Vuetify computed for the drawer
  // (which already accounts for app bars / layout offsets) so the sticky
  // footer never overflows past the visible area.
  :deep(.v-navigation-drawer__content) {
    display: flex;
    flex-direction: column;
  }
}

.app-drawer {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  background-color: rgb(var(--v-theme-white));

  &__header {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    column-gap: 20px;
    flex: 0 0 auto;
    padding: 24px;

    &--with-tabs {
      padding-bottom: 8px;
    }
  }

  &__title {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    column-gap: 8px;
  }

  &__tabs {
    flex: 0 0 auto;
    padding: 0 24px;
    border-bottom: get-border();

    :deep(.v-slide-group__content) {
      column-gap: 12px;
    }

    // The repeated `.v-tab` matches the specificity of Vuetify's own `.v-tab.v-tab.v-btn`
    :deep(.v-tab.v-tab.v-btn) {
      @include text ('heading-xs-bold');
      color: rgb(var(--v-theme-text-tertiary));
      min-width: 0;
      padding: 0 8px;
      border-radius: get-radius('border-radius-2') get-radius('border-radius-2') 0 0;

      .v-btn__overlay,
      .v-btn__underlay {
        display: none;
      }

      &:hover {
        background-color: rgb(var(--v-theme-surface-secondary));
      }
    }

    :deep(.v-tab__slider) {
      bottom: 1px;
      background: rgb(var(--v-theme-primary));
    }
  }

  &__body {
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
  }

  &__content {
    height: 100%;
    overflow-y: auto;
    padding: 24px;
    color: rgb(var(--v-theme-grey-800));
    letter-spacing: normal;
    line-height: normal;

    &--flush {
      padding: 0;
    }

    &--fill {
      overflow: hidden;

      // The active tab is given the body height and scrolls what overflows it, so a content
      // that lays itself out against that height keeps its own bars in place.
      :deep(.v-window),
      :deep(.v-window__container),
      :deep(.v-window-item--active) {
        height: 100%;
        overflow-y: auto;
      }
    }
  }

  &__fade {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 40px;
    pointer-events: none;
    background: linear-gradient(to bottom, rgba(var(--v-theme-white), 0), rgb(var(--v-theme-white)));
  }

  &__actions {
    display: flex;
    align-items: center;
    // Actions sit on the right edge, so neither the default footer nor a custom one needs a
    // spacer to push them there.
    justify-content: flex-end;
    flex: 0 0 auto;
    width: 100%;
    background-color: rgb(var(--v-theme-white));
    border-top: get-border();
    padding: 24px;
    column-gap: 16px;
  }

  // The footer link is the one element that stays on the left, opposite the actions
  &__footer-link {
    margin-right: auto;
  }
}

@media #{map-get($display-breakpoints, 'xs')} {
  .app-drawer {
    &__header {
      padding: 12px !important;
    }

    &__tabs {
      padding: 0 12px !important;
    }

    &__content {
      padding: 12px !important;
    }

    &__actions {
      padding: 12px !important;
    }
  }
}
</style>
